import { useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Muted, PageHeader, Section, StatusBadge, Tabs } from '@/components/ui'
import {
  fecharVaga,
  fetchEmpresasGrupo,
  fetchPipeline,
  fetchVaga,
  patchVaga,
  pausarVaga,
  publicarVaga,
} from './api'
import { PipelineBoard } from './components/PipelineBoard'
import {
  formatLocal,
  formatSalario,
  prioridadeLabel,
  statusLabel,
  statusTone,
} from './utils'

const TABS = [
  { id: 'detalhe', label: 'Detalhe' },
  { id: 'pipeline', label: 'Pipeline' },
] as const

export function VagaDetailPage() {
  const { vagaId = '' } = useParams()
  const { canEdit } = useAuth()
  const qc = useQueryClient()
  const [params, setParams] = useSearchParams()
  const [msg, setMsg] = useState('')
  const [msgTone, setMsgTone] = useState<'success' | 'danger'>('success')
  const tab = params.get('tab') === 'pipeline' ? 'pipeline' : 'detalhe'

  const vaga = useQuery({
    queryKey: ['ats', 'vaga', vagaId],
    queryFn: () => fetchVaga(vagaId),
    enabled: Boolean(vagaId),
  })

  const pipeline = useQuery({
    queryKey: ['ats', 'pipeline', vagaId],
    queryFn: () => fetchPipeline(vagaId),
    enabled: Boolean(vagaId) && tab === 'pipeline',
  })

  const empresas = useQuery({
    queryKey: ['ats', 'empresas-grupo'],
    queryFn: fetchEmpresasGrupo,
    enabled: Boolean(vagaId),
  })

  const statusMut = useMutation({
    mutationFn: async (kind: 'publicar' | 'pausar' | 'fechar') => {
      if (kind === 'publicar') return publicarVaga(vagaId)
      if (kind === 'pausar') return pausarVaga(vagaId)
      return fecharVaga(vagaId)
    },
    onSuccess: async (_res, kind) => {
      setMsgTone('success')
      setMsg(
        kind === 'publicar'
          ? 'Vaga publicada.'
          : kind === 'pausar'
            ? 'Vaga pausada.'
            : 'Vaga fechada.',
      )
      await qc.invalidateQueries({ queryKey: ['ats', 'vaga', vagaId] })
      await qc.invalidateQueries({ queryKey: ['ats', 'vagas'] })
      await qc.invalidateQueries({ queryKey: ['ats', 'dashboard'] })
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao atualizar status.')
    },
  })

  const empresaMut = useMutation({
    mutationFn: (empresa_grupo: string) => patchVaga(vagaId, { empresa_grupo }),
    onSuccess: async () => {
      setMsgTone('success')
      setMsg('Empresa da vaga atualizada — aparece no portal Carreiras.')
      await qc.invalidateQueries({ queryKey: ['ats', 'vaga', vagaId] })
      await qc.invalidateQueries({ queryKey: ['ats', 'vagas'] })
    },
    onError: (err) => {
      setMsgTone('danger')
      setMsg(err instanceof ApiError ? err.message : 'Falha ao atualizar empresa.')
    },
  })

  if (vaga.isLoading) {
    return <Muted>Carregando vaga…</Muted>
  }
  if (vaga.isError || !vaga.data) {
    return (
      <div className="hi-page">
        <Alert tone="danger">
          {vaga.error instanceof ApiError ? vaga.error.message : 'Vaga não encontrada.'}
        </Alert>
        <div className="hi-actions">
          <Link className="btn btn-secondary" to="/app/recrutamento">
            Voltar
          </Link>
        </div>
      </div>
    )
  }

  const d = vaga.data
  const editable = canEdit('recrutamento')

  return (
    <section className="hi-page">
      <PageHeader
        backTo={{ to: '/app/recrutamento', label: 'Recrutamento' }}
        title={d.titulo}
        subtitle={
          <>
            <StatusBadge tone={statusTone(d.status)}>{statusLabel(d.status)}</StatusBadge>{' '}
            {d.empresa_grupo_nome ? `${d.empresa_grupo_nome} · ` : ''}
            {formatLocal(d.cidade, d.uf)} · prioridade {prioridadeLabel(d.prioridade)}
          </>
        }
        actions={
          <>
            {editable && (d.status === 'rascunho' || d.status === 'pausada') ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={statusMut.isPending}
                onClick={() => statusMut.mutate('publicar')}
              >
                Publicar
              </button>
            ) : null}
            {editable && d.status === 'aberta' ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={statusMut.isPending}
                onClick={() => statusMut.mutate('pausar')}
              >
                Pausar
              </button>
            ) : null}
            {editable && d.status !== 'fechada' ? (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={statusMut.isPending}
                onClick={() => {
                  if (!window.confirm('Fechar esta vaga?')) return
                  statusMut.mutate('fechar')
                }}
              >
                Fechar
              </button>
            ) : null}
            <a className="btn btn-secondary" href={legacyAppUrl(`#/recrutamento`)}>
              Legado
            </a>
          </>
        }
      />

      {msg ? <Alert tone={msgTone}>{msg}</Alert> : null}

      <Tabs
        items={TABS}
        value={tab}
        onChange={(id) => setParams(id === 'pipeline' ? { tab: 'pipeline' } : {})}
        label="Detalhe da vaga"
      />

      {tab === 'detalhe' ? (
        <div className="hi-stack">
          <Section>
            <ul className="hi-meta">
              <li>{d.departamento_nome || 'Sem departamento'}</li>
              <li>{d.cargo_nome || 'Sem cargo'}</li>
              <li>{(d.modalidade || '-').replace('_', ' ')}</li>
              <li>{(d.tipo_contrato || '-').toUpperCase()}</li>
              <li>{formatSalario(d.salario_min, d.salario_max, d.salario_oculto)}</li>
              <li>{d.total_candidatos ?? 0} candidatos</li>
            </ul>
            {editable ? (
              <label className="hi-field" style={{ display: 'block', marginTop: '0.75rem' }}>
                Empresa no Carreiras
                <select
                  value={d.empresa_grupo || ''}
                  disabled={empresaMut.isPending || empresas.isLoading}
                  onChange={(e) => {
                    const next = e.target.value
                    if (!next) return
                    empresaMut.mutate(next)
                  }}
                >
                  <option value="">Selecione a marca…</option>
                  {(empresas.data || []).map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.nome}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </Section>
          {d.descricao ? (
            <Section title="Descrição">
              <p className="hi-prose">{d.descricao}</p>
            </Section>
          ) : null}
          {d.requisitos ? (
            <Section title="Requisitos">
              <pre className="hi-prose">{d.requisitos}</pre>
            </Section>
          ) : null}
          {d.beneficios ? (
            <Section title="Benefícios">
              <p className="hi-prose">{d.beneficios}</p>
            </Section>
          ) : null}
        </div>
      ) : null}

      {tab === 'pipeline' ? (
        <div className="hi-stack">
          {pipeline.isLoading ? <Muted>Carregando pipeline…</Muted> : null}
          {pipeline.isError ? (
            <Alert tone="danger">
              {pipeline.error instanceof ApiError
                ? pipeline.error.message
                : 'Falha ao carregar pipeline.'}
            </Alert>
          ) : null}
          {pipeline.data ? <PipelineBoard vagaId={vagaId} pipeline={pipeline.data} /> : null}
        </div>
      ) : null}
    </section>
  )
}

export default VagaDetailPage
